import { useEffect, useState, type ReactNode } from "react";
import ThemeToggle from "../ThemeToggle";
import ErrorPopup from "../ErrorPopup";
import SongSimpliLogo from "../SongSimpliLogo";
import { registerErrorPopupHandler } from "../../utils/errorPopupBridge";
import { ChevronDownIcon } from "./SongIcons";

const LIGHT_BACKGROUND =
  "linear-gradient(180deg, #ffffff 0%, #f4ecff 62%, #d5e6f7 100%)";
const DARK_BACKGROUND =
  "radial-gradient(ellipse 80% 65% at 50% 109%, #FF00D633 0%, rgba(255, 0, 214, 0.2) 50%, transparent 100%)";

function getInitial(name: string): string {
  return name.trim().charAt(0).toUpperCase() || "?";
}

interface SongPageShellProps {
  /** Buyer name for the header pill. The pill is hidden when absent. */
  userName?: string;
  /** Scrollable page body. */
  children: ReactNode;
  /** Pinned action bar below the body. */
  footer?: ReactNode;
}

/**
 * Shared chrome for the song order pages: fixed header, gradient scroll area
 * and a pinned footer action bar.
 */
export default function SongPageShell({
  userName,
  children,
  footer,
}: SongPageShellProps) {
  // Matches App.tsx / OnboardingScreen: dark unless explicitly set to light.
  const [isDark, setIsDark] = useState(
    () => localStorage.getItem("theme") !== "light",
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showErrorPopup, setShowErrorPopup] = useState(false);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("dark", isDark);
    localStorage.setItem("theme", isDark ? "dark" : "light");
  }, [isDark]);

  // Surfaces the errors the axios interceptor reports, same as App.tsx.
  useEffect(() => {
    registerErrorPopupHandler((message) => {
      setErrorMessage(message);
      setShowErrorPopup(true);
    });
    return () => registerErrorPopupHandler(null);
  }, []);

  return (
    <div className="flex h-full w-full flex-col overflow-hidden bg-white dark:bg-[#16283d]"  style={{ background: isDark ? DARK_BACKGROUND : LIGHT_BACKGROUND }}>
      <header className="nav-header flex h-16 shrink-0 items-center justify-between border-b-[1.5px] sm:border-b-[0px] border-[#dbdee1] bg-white px-4 sm:px-5 dark:border-[#264a73] dark:bg-[#16283d]">
        <SongSimpliLogo
          isDark={isDark}
          wrapperClassName="block h-10 w-[120px] sm:h-[40px] sm:w-[200px]"
          imgClassName="block h-full w-full object-contain"
        />

        <div className="flex items-center gap-2.5">
          {userName && (
            <div className="flex items-center gap-2 rounded-full border-[1.5px] border-[#dbdee1] bg-white py-1 pl-1 pr-3 dark:border-[#264a73] dark:bg-[#1e3554]">
              <span className="flex size-7 items-center justify-center rounded-full bg-[#16283d] text-[13px] font-bold text-white dark:bg-[#0e76ff]">
                {getInitial(userName)}
              </span>
              <span className="text-[14px] font-semibold text-[#16283d] dark:text-[#fdfdfd]">
                {userName}
              </span>
              <ChevronDownIcon className="text-[#687585] dark:text-[#a1a7b0]" />
            </div>
          )}
          <ThemeToggle isDark={isDark} onToggle={() => setIsDark((d) => !d)} />
        </div>
      </header>

      <div
        className="custom-scrollbar min-h-0 flex-1 overflow-y-auto"
       
      >
        {children}
      </div>

      {footer && (
        <div className="shrink-0 px-3 pt-4 pb-3 sm:pb-25">
          {footer}
        </div>
      )}

      {showErrorPopup && (
        <ErrorPopup
          message={errorMessage}
          onClose={() => {
            setShowErrorPopup(false);
            setErrorMessage(null);
          }}
        />
      )}
    </div>
  );
}
